import {
  getDay,
  getAccommodationById,
  getPlaceById
} from '../firebase/firestoreService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseInit';
import { documentService } from '@trip-planner-monorepo/documents';
import { connectionRegistryService } from './connectionRegistryService';
import { linkRegistryConfigService } from './linkRegistryConfigService';
import { shouldReadFromRegistry, shouldFallbackToLegacyReads } from '../utils/linkRegistryModeUtils';
import { isActiveLink } from '../utils/linkCanonicalUtils';
import { formatDayDisplayLabel } from '../utils/dayDisplayUtils';
import { LINK_TYPES } from '../config/entityTypeRegistry';
import {
  createAccommodationReferenceBlock,
  createPlaceReferenceBlock,
  createTravelReferenceBlock
} from '../utils/contentBlockUtils';

/**
 * @typedef {'itinerary' | 'related' | 'planning' | 'linker' | 'pdf'} LinkSurface
 */

/**
 * @param {import('../types/tripLink.types').TripLinkDocument[]} links
 * @param {LinkSurface} surface
 * @returns {import('../types/tripLink.types').TripLinkDocument[]}
 */
export function filterLinksForSurface(links, surface) {
  const active = (links || []).filter((link) => isActiveLink(link) && link.linkIntent !== 'rejected');

  switch (surface) {
  case 'itinerary':
    return active.filter((link) => link.displayMode === 'itinerary_entry');
  case 'planning':
    return active.filter(
      (link) => ['candidate', 'related_only'].includes(link.displayMode) || link.linkIntent === 'proposed'
    );
  case 'related':
    return active.filter(
      (link) => link.displayMode !== 'itinerary_entry' && link.displayMode !== 'hidden'
    );
  case 'linker':
    return active.filter((link) => link.displayMode !== 'hidden');
  case 'pdf':
  default:
    return active.filter((link) => link.displayMode !== 'hidden');
  }
}

/**
 * @param {Object} link
 * @param {string} hostEntityType
 * @param {string} hostEntityId
 * @returns {{ type: string, id: string } | null}
 */
export function getOtherEndpoint(link, hostEntityType, hostEntityId) {
  if (!link?.entityLow || !link?.entityHigh) {
    return null;
  }
  if (link.entityLow.type === hostEntityType && link.entityLow.id === hostEntityId) {
    return link.entityHigh;
  }
  if (link.entityHigh.type === hostEntityType && link.entityHigh.id === hostEntityId) {
    return link.entityLow;
  }
  return null;
}

/**
 * @param {Object|null} entity
 * @param {string} entityType
 * @returns {string}
 */
export function resolveEntityDisplayName(entity, entityType) {
  if (!entity) {
    return 'Unknown';
  }
  switch (entityType) {
  case 'place':
    return entity.name || 'Place';
  case 'accommodation':
    return entity.details?.name || entity.summary || entity.name || 'Accommodation';
  case 'travel':
    return entity.summary || entity.details?.type || 'Travel';
  case 'day':
    return formatDayDisplayLabel(entity);
  case 'document':
    return entity.title || entity.name || 'Document';
  default:
    return 'Entity';
  }
}

/**
 * @param {string} tripId
 * @param {string} entityType
 * @param {string} entityId
 * @returns {string}
 */
export function buildEntityRoute(tripId, entityType, entityId) {
  switch (entityType) {
  case 'place':
    return `/trips/${tripId}/places/${entityId}`;
  case 'accommodation':
    return `/trips/${tripId}/accommodation/${entityId}`;
  case 'travel':
    return `/trips/${tripId}/travel/${entityId}`;
  case 'day':
    return `/trips/${tripId}/day/${entityId}`;
  case 'document':
    return `/trips/${tripId}/documents/${entityId}`;
  default:
    return '#';
  }
}

async function fetchEntityById(userId, tripId, entityType, entityId) {
  switch (entityType) {
  case 'day':
    return getDay(userId, tripId, entityId);
  case 'accommodation':
    return getAccommodationById(userId, tripId, entityId);
  case 'place':
    return getPlaceById(userId, tripId, entityId);
  case 'travel': {
    const travelDoc = await getDoc(doc(db, 'users', userId, 'trips', tripId, 'travel', entityId));
    return travelDoc.exists() ? { id: travelDoc.id, ...travelDoc.data() } : null;
  }
  case 'document':
    return documentService.getDocument(userId, tripId, entityId);
  default:
    return null;
  }
}

/**
 * @param {Object} link
 * @param {string} hostEntityType
 * @param {string} hostEntityId
 * @param {string} tripId
 * @param {Object|null} relatedEntity
 * @param {{ type: string, id: string }} relatedEndpoint
 * @returns {Object}
 */
export function buildLinkRenderModel(link, hostEntityType, hostEntityId, tripId, relatedEntity, relatedEndpoint) {
  const entityName = resolveEntityDisplayName(relatedEntity, relatedEndpoint.type);
  return {
    linkId: link.linkId || link.id,
    entityType: relatedEndpoint.type,
    entityId: relatedEndpoint.id,
    entityName,
    linkType: link.linkType,
    route: buildEntityRoute(tripId, relatedEndpoint.type, relatedEndpoint.id),
    subtitle: link.displayContext || link.linkType,
    badge: link.linkIntent === 'proposed' ? 'Proposed' : null
  };
}

/**
 * @param {string} userId
 * @param {string} tripId
 * @param {string} hostEntityType
 * @param {string} hostEntityId
 * @param {{ surface?: LinkSurface, excludeTypes?: string[] }} [options]
 * @returns {Promise<{ source: 'registry' | 'legacy', models: Object[] }>}
 */
export async function getRelatedEntityModels(userId, tripId, hostEntityType, hostEntityId, options = {}) {
  const { surface = 'related', excludeTypes = [] } = options;
  const config = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);

  if (!shouldReadFromRegistry(config)) {
    return { source: 'legacy', models: [] };
  }

  const links = await connectionRegistryService.getLinksForEntity(
    userId,
    tripId,
    hostEntityType,
    hostEntityId
  );
  const filtered = filterLinksForSurface(links, surface).filter((link) => {
    const other = getOtherEndpoint(link, hostEntityType, hostEntityId);
    return other && !excludeTypes.includes(other.type);
  });

  if (filtered.length === 0 && config.readMode !== 'registry_only') {
    return { source: 'legacy', models: [] };
  }

  const models = [];
  const entityCache = new Map();

  const fetchCachedEntity = (entityType, entityId) => {
    const cacheKey = `${entityType}:${entityId}`;
    if (!entityCache.has(cacheKey)) {
      entityCache.set(cacheKey, fetchEntityById(userId, tripId, entityType, entityId));
    }
    return entityCache.get(cacheKey);
  };

  for (const link of filtered) {
    const relatedEndpoint = getOtherEndpoint(link, hostEntityType, hostEntityId);
    if (!relatedEndpoint) {
      continue;
    }
    const relatedEntity = await fetchCachedEntity(relatedEndpoint.type, relatedEndpoint.id);
    models.push(
      buildLinkRenderModel(link, hostEntityType, hostEntityId, tripId, relatedEntity, relatedEndpoint)
    );
  }

  return { source: 'registry', models };
}

/**
 * Registry-primary related documents for hosts (travel, accommodation, day, place, etc.).
 * @param {string} userId
 * @param {string} tripId
 * @param {string} hostEntityType
 * @param {string} hostEntityId
 * @returns {Promise<{ source: 'registry' | 'legacy', documents: Array<{ documentId: string, documentTitle: string, linkId?: string }> }>}
 */
export async function getRelatedDocumentModels(userId, tripId, hostEntityType, hostEntityId) {
  const { source, models } = await getRelatedEntityModels(userId, tripId, hostEntityType, hostEntityId, {
    surface: 'related'
  });

  const documents = (models || [])
    .filter((model) => model.entityType === 'document')
    .map((model) => ({
      documentId: model.entityId,
      documentTitle: model.entityName,
      linkId: model.linkId
    }));

  return { source, documents };
}

const ITINERARY_TYPED_REFERENCE_TYPES = new Set([
  'accommodation_reference',
  'travel_reference',
  'place_reference',
  'day_reference'
]);

/**
 * @param {Object} accommodation
 * @param {string|null} displayContext
 * @returns {string}
 */
export function buildAccommodationItineraryDisplayText(accommodation, displayContext = null) {
  const accommodationName = resolveEntityDisplayName(accommodation, 'accommodation');
  const city = accommodation?.details?.city;
  let displayText = city
    ? `Stay at ${accommodationName}: ${city}`
    : `Stay at ${accommodationName}`;

  if (displayContext === 'check-in') {
    displayText += ' (Check-in)';
  } else if (displayContext === 'check-out') {
    displayText += ' (Check-out)';
  }

  return displayText;
}

/**
 * @param {Object} link
 * @param {string} hostEntityType
 * @param {string} hostEntityId
 * @param {Object|null} relatedEntity
 * @param {{ type: string, id: string }} relatedEndpoint
 * @param {string|null} [registryPlaceId] - The accommodation's `located_in` place, if already
 *   resolved from the registry. Preferred over `relatedEntity.placeReference.placeId`, since
 *   that legacy field is never populated on registry-routed trips (Issue 1 writes located_in
 *   to the registry only) — without this, the itinerary block's place link is silently dropped.
 * @returns {Object|null}
 */
export function linkToItineraryContentBlock(
  link,
  hostEntityType,
  hostEntityId,
  relatedEntity,
  relatedEndpoint,
  registryPlaceId = null
) {
  if (!relatedEntity || !relatedEndpoint) {
    return null;
  }

  const linkId = link.linkId || link.id;
  const registryMetadata = {
    registryLinkId: linkId,
    source: 'connection_registry'
  };

  if (link.linkType === LINK_TYPES.STAYS_ON && relatedEndpoint.type === 'accommodation') {
    const displayText = buildAccommodationItineraryDisplayText(relatedEntity, link.displayContext);
    return {
      ...createAccommodationReferenceBlock(
        relatedEndpoint.id,
        resolveEntityDisplayName(relatedEntity, 'accommodation'),
        link.displayContext || null,
        registryMetadata
      ),
      displayText,
      placeId: registryPlaceId || relatedEntity.placeReference?.placeId || null
    };
  }

  switch (relatedEndpoint.type) {
  case 'travel':
    return {
      ...createTravelReferenceBlock(
        relatedEndpoint.id,
        resolveEntityDisplayName(relatedEntity, 'travel'),
        relatedEntity.details?.type || relatedEntity.type || null,
        registryMetadata
      )
    };
  case 'place':
    return {
      ...createPlaceReferenceBlock(
        relatedEndpoint.id,
        resolveEntityDisplayName(relatedEntity, 'place'),
        link.displayContext || null,
        registryMetadata
      )
    };
  default:
    return null;
  }
}

/**
 * @param {Array<Object>} legacyContent
 * @returns {Array<Object>}
 */
export function stripLegacyItineraryReferenceBlocks(legacyContent = []) {
  return (legacyContent || []).filter((block) => !ITINERARY_TYPED_REFERENCE_TYPES.has(block.type));
}

/**
 * Shared resolution core for both the single-day and batched itinerary resolvers below —
 * given a day and the itinerary-surface links that already apply to it, decides whether to
 * fall back to legacy content or build registry-derived content blocks. Kept as one function
 * so the two call sites can never quietly diverge in behavior.
 * @param {{ id: string, content?: Array<Object> }} day
 * @param {Array<Object>} dayItineraryLinks
 * @param {Object} config
 * @param {(entityType: string, entityId: string) => Promise<Object|null>} fetchCachedEntity
 * @param {(accommodationId: string) => Promise<string|null>} resolveAccommodationPlaceId
 * @returns {Promise<{ source: 'registry' | 'legacy', content: Array<Object> }>}
 */
async function resolveDayContentFromLinks(day, dayItineraryLinks, config, fetchCachedEntity, resolveAccommodationPlaceId) {
  const nonDocumentLegacy = (day.content || []).filter((block) => block.type !== 'document_reference');

  if (dayItineraryLinks.length === 0) {
    if (shouldFallbackToLegacyReads(config)) {
      return { source: 'legacy', content: nonDocumentLegacy };
    }
    return { source: 'registry', content: stripLegacyItineraryReferenceBlocks(nonDocumentLegacy) };
  }

  const registryBlocks = [];
  for (const link of dayItineraryLinks) {
    const relatedEndpoint = getOtherEndpoint(link, 'day', day.id);
    if (!relatedEndpoint) {
      continue;
    }
    const relatedEntity = await fetchCachedEntity(relatedEndpoint.type, relatedEndpoint.id);
    const registryPlaceId = relatedEndpoint.type === 'accommodation'
      ? await resolveAccommodationPlaceId(relatedEndpoint.id)
      : null;
    const block = linkToItineraryContentBlock(link, 'day', day.id, relatedEntity, relatedEndpoint, registryPlaceId);
    if (block) {
      registryBlocks.push(block);
    }
  }

  if (registryBlocks.length === 0 && shouldFallbackToLegacyReads(config)) {
    return { source: 'legacy', content: nonDocumentLegacy };
  }

  return {
    source: 'registry',
    content: [...stripLegacyItineraryReferenceBlocks(nonDocumentLegacy), ...registryBlocks]
  };
}

function makeEntityCacheFetcher(userId, tripId) {
  const entityCache = new Map();
  return (entityType, entityId) => {
    const cacheKey = `${entityType}:${entityId}`;
    if (!entityCache.has(cacheKey)) {
      entityCache.set(cacheKey, fetchEntityById(userId, tripId, entityType, entityId));
    }
    return entityCache.get(cacheKey);
  };
}

function getLocatedInPlaceId(link, accommodationId) {
  const placeEndpoint = getOtherEndpoint(link, 'accommodation', accommodationId);
  return placeEndpoint?.type === 'place' ? placeEndpoint.id : null;
}

/**
 * Per-accommodation, registry-backed `located_in` place lookup for the single-day resolver —
 * queries the registry once per distinct accommodation encountered (cached), since the day's
 * own link set doesn't include the accommodation↔place link.
 */
function makeAccommodationPlaceIdResolver(userId, tripId) {
  const cache = new Map();
  return (accommodationId) => {
    if (!cache.has(accommodationId)) {
      cache.set(accommodationId, (async () => {
        const links = await connectionRegistryService.getLinksForEntity(userId, tripId, 'accommodation', accommodationId);
        const locatedIn = (links || []).find((link) => link.linkType === LINK_TYPES.LOCATED_IN && isActiveLink(link));
        return locatedIn ? getLocatedInPlaceId(locatedIn, accommodationId) : null;
      })());
    }
    return cache.get(accommodationId);
  };
}

/**
 * Same lookup as {@link makeAccommodationPlaceIdResolver}, but built from a trip's full link
 * list that's already been fetched (the batched resolver's single `getAllLinks` call) — no
 * extra registry queries needed.
 */
function makeAccommodationPlaceIdResolverFromLinks(allLinks) {
  const placeIdByAccommodationId = new Map();
  allLinks.forEach((link) => {
    if (link.linkType !== LINK_TYPES.LOCATED_IN || !isActiveLink(link)) {
      return;
    }
    const accEndpoint = link.entityLow?.type === 'accommodation'
      ? link.entityLow
      : link.entityHigh?.type === 'accommodation' ? link.entityHigh : null;
    if (!accEndpoint) {
      return;
    }
    const placeId = getLocatedInPlaceId(link, accEndpoint.id);
    if (placeId) {
      placeIdByAccommodationId.set(accEndpoint.id, placeId);
    }
  });
  return async (accommodationId) => placeIdByAccommodationId.get(accommodationId) || null;
}

/**
 * Registry-primary day itinerary content for DayDetailPage / day previews.
 * @param {string} userId
 * @param {string} tripId
 * @param {string} dayId
 * @param {Array<Object>} [legacyContent]
 * @returns {Promise<{ source: 'registry' | 'legacy', content: Array<Object> }>}
 */
export async function resolveDayItineraryContent(userId, tripId, dayId, legacyContent = []) {
  const config = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
  const day = { id: dayId, content: legacyContent };
  const nonDocumentLegacy = (legacyContent || []).filter((block) => block.type !== 'document_reference');

  if (!shouldReadFromRegistry(config)) {
    return { source: 'legacy', content: nonDocumentLegacy };
  }

  const links = await connectionRegistryService.getLinksForEntity(userId, tripId, 'day', dayId);
  const itineraryLinks = filterLinksForSurface(links, 'itinerary');
  const fetchCachedEntity = makeEntityCacheFetcher(userId, tripId);
  const resolveAccommodationPlaceId = makeAccommodationPlaceIdResolver(userId, tripId);

  return resolveDayContentFromLinks(day, itineraryLinks, config, fetchCachedEntity, resolveAccommodationPlaceId);
}

/**
 * Batched sibling of {@link resolveDayItineraryContent} for list views (e.g. the Days list
 * page) that need registry-accurate itinerary content for every day in a trip at once.
 * Fetches the trip's links exactly once via `getAllLinks` and groups them by day, rather than
 * issuing one `getLinksForEntity` query per day — the two resolvers share the same content
 * resolution core (`resolveDayContentFromLinks`), so a list rendered this way always matches
 * what each day's own detail page would show.
 * @param {string} userId
 * @param {string} tripId
 * @param {Array<{ id: string, content?: Array<Object> }>} days
 * @returns {Promise<Map<string, { source: 'registry' | 'legacy', content: Array<Object> }>>}
 */
export async function resolveDayItineraryContentForTrip(userId, tripId, days) {
  const config = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
  const resultByDayId = new Map();

  if (!shouldReadFromRegistry(config)) {
    days.forEach((day) => {
      const nonDocumentLegacy = (day.content || []).filter((block) => block.type !== 'document_reference');
      resultByDayId.set(day.id, { source: 'legacy', content: nonDocumentLegacy });
    });
    return resultByDayId;
  }

  const allLinks = await connectionRegistryService.getAllLinks(userId, tripId);
  const itineraryLinks = filterLinksForSurface(allLinks, 'itinerary');

  const linksByDayId = new Map();
  itineraryLinks.forEach((link) => {
    const dayEndpoint = link.entityLow?.type === 'day'
      ? link.entityLow
      : link.entityHigh?.type === 'day' ? link.entityHigh : null;
    if (!dayEndpoint) {
      return;
    }
    if (!linksByDayId.has(dayEndpoint.id)) {
      linksByDayId.set(dayEndpoint.id, []);
    }
    linksByDayId.get(dayEndpoint.id).push(link);
  });

  const fetchCachedEntity = makeEntityCacheFetcher(userId, tripId);
  const resolveAccommodationPlaceId = makeAccommodationPlaceIdResolverFromLinks(allLinks);

  for (const day of days) {
    const dayLinks = linksByDayId.get(day.id) || [];
    // eslint-disable-next-line no-await-in-loop
    const resolved = await resolveDayContentFromLinks(day, dayLinks, config, fetchCachedEntity, resolveAccommodationPlaceId);
    resultByDayId.set(day.id, resolved);
  }

  return resultByDayId;
}

export const linkPresentationService = {
  filterLinksForSurface,
  getOtherEndpoint,
  resolveEntityDisplayName,
  buildEntityRoute,
  buildLinkRenderModel,
  getRelatedEntityModels,
  getRelatedDocumentModels,
  buildAccommodationItineraryDisplayText,
  linkToItineraryContentBlock,
  stripLegacyItineraryReferenceBlocks,
  resolveDayItineraryContent,
  resolveDayItineraryContentForTrip
};

export default linkPresentationService;
